/**
 * SSE (Server-Sent Events) Hook
 * 基于全局 sseClient 单例订阅事件，不再自行建立 EventSource 连接，
 * 避免与 Layout / useTaskManager 重复建立长连接。
 */

import { useEffect, useRef } from 'react';
import { sseClient } from '../lib/sseClient';

// SSE 事件类型
export type SSEEventType = 'task_lifecycle' | 'task_stream' | 'heartbeat' | 'system' | 'notification';

// 通知事件数据
export interface NotificationData {
  id: number;
  title: string;
  content: string;
  type: 'SYSTEM' | 'PAYMENT' | 'AI';
  imageUrl?: string | null;
  link?: string | null;
  isRead: boolean;
  createdAt: string;
}

// 任务生命周期事件数据
export interface TaskLifecycleData {
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress?: number;
  result?: string;
  error?: string;
  metadata?: Record<string, unknown>;
}

// 任务流式事件数据
export interface TaskStreamData {
  chunk: string;
  done: boolean;
  totalChars?: number;
}

// 系统事件数据
export interface SystemData {
  message: string;
  level: 'info' | 'warning' | 'error';
}

// SSE 事件回调
export interface SSECallbacks {
  onNotification?: (data: NotificationData) => void;
  onTaskLifecycle?: (taskId: string, data: TaskLifecycleData) => void;
  onTaskStream?: (taskId: string, data: TaskStreamData) => void;
  onSystem?: (data: SystemData) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Error) => void;
}

/**
 * SSE Hook
 * 订阅全局 sseClient 分发的事件并触发对应回调。
 */
export function useSSE(callbacks: SSECallbacks = {}) {
  const callbacksRef = useRef(callbacks);

  // 更新 callbacks ref
  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  useEffect(() => {
    const unsubNotification = sseClient.subscribe('notification', (data) => {
      callbacksRef.current.onNotification?.(data.payload as NotificationData);
    });
    const unsubSystem = sseClient.subscribe('system', (data) => {
      callbacksRef.current.onSystem?.(data.payload as SystemData);
    });
    const unsubTaskLifecycle = sseClient.subscribe('task_lifecycle', (data) => {
      if (data.taskId) {
        callbacksRef.current.onTaskLifecycle?.(String(data.taskId), data.payload as TaskLifecycleData);
      }
    });
    const unsubTaskStream = sseClient.subscribe('task_stream', (data) => {
      if (data.taskId) {
        callbacksRef.current.onTaskStream?.(String(data.taskId), data.payload as TaskStreamData);
      }
    });

    return () => {
      unsubNotification();
      unsubSystem();
      unsubTaskLifecycle();
      unsubTaskStream();
    };
  }, []);

  return {
    connect: () => {},
    disconnect: () => {},
    isConnected: false,
  };
}

export default useSSE;
